import { __awaiter } from "tslib";
import CommonUtils from "./common-utils";
import { Preferences } from "@3ds/common-preferences";
class CommonRequest {
    constructor() { }
    /**
     * UWA Proxy urls indexed by types.
     */
    static get proxies() {
        // Native app proxies
        const nativeProxies = Preferences.getNativeAppRequireConfig();
        if (nativeProxies && nativeProxies.proxies) {
            return nativeProxies.proxies;
        }
        // UWA proxies
        const hosts = window.UWA && window.UWA.hosts || {};
        const exposition = hosts.exposition;
        const expositionProxy = exposition + '/proxy/';
        const _proxies = hosts.proxies || {};
        // console.log('DEBUG common-request proxies', _proxies)
        return Object.assign(Object.assign({}, _proxies), {
            ajax: expositionProxy + 'ajax',
            resolve: expositionProxy + 'resolve',
            xml: expositionProxy + 'xml',
            spreadsheet: expositionProxy + 'spreadsheet',
            soap: expositionProxy + 'soap',
            feed: expositionProxy + 'feed',
            icon: expositionProxy + 'icon',
            richIcon: expositionProxy + 'richIcon',
            rss: expositionProxy + 'feed' // Deprecated alias support
        });
    }
    static normalizeRequestHeaders(request) {
        let strHeaders = {}; // HTP1: need to filter headers (ref. WP)
        request.headers.forEach((header, key) => {
            // console.log("DEBUG proxifyRequest check header", header)
            strHeaders[key] = header;
        });
        return strHeaders;
    }
    static checkHasProxyPath(url) {
        // Check native app proxies
        if (Preferences.isNativeApp()) {
            const nativeProxies = Preferences.getNativeAppRequireConfig();
            if (nativeProxies && nativeProxies.proxies) {
                for (const proxy in nativeProxies.proxies) {
                    if (url.startsWith(nativeProxies.proxies[proxy]))
                        return true;
                }
            }
        }
        return false;
    }
    /**
     * This method is used to create a Request that can be proxified to do CORS requests.
     * It supports the 2 proxy options:
     *      - ajax (for non-authenticated request)
     *      - passport (for authenticated request)
     * It allows to create a proxified Request that use these proxies.
     *
     * __Specific proxy options__:
     *
     * You can pass specific options into an object that have the same name than the used proxy
     * to configure proxy behavior, see Proxy Examples bellow for detailed usage.
     *
     * | Proxy       |  Description                                |
     * | ------------| --------------------------------------------|
     * | `ajax`      | cors request without authenticated session  |
     * | `passport`  | authenticated session request               |
     *
     *
     * @param {String}  url - the URL of the data source
     * @param {Object}  options - NormalizedOptions header object
     * @param {String}  [options.method=GET] - GET, POST (in uppercase!)
     * @param {Object}  [options.body={}] - GET or POST params as object
     * @param {String}  [options.proxy=false] - ajax, passport
     * @param {Object}  [options.headers] - Headers object.
     * @param {Request} originRequest - Original request.
     *
     * @return {Request} A request that is proxified or not
     * If the options.proxy is set && the proxification condition is satisfied, a new request will be created with a proxified URL.
     * Otherwise, the original request will be return.
     *
     * @alias CommonRequest.request
     */
    static request(url, options, originRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            // First argument is required.
            if (!url) {
                throw new Error('Bad or missing url argument.');
            }
            // If not on native app or on same domain then do not proxify url
            if (!Preferences.isNativeApp() || CommonUtils.matchUrl(url, window.location))
                return originRequest;
            // Second argument is and object anyway
            options = options || {};
            var request, 
            // Default Options/Values
            originalUrl = url, currentUrl = window.location, defaultProxy = 'ajax', requestOptions = options;
            if (requestOptions.headers && !requestOptions.normalizedHeaders) {
                requestOptions.normalizedHeaders = CommonRequest.normalizeRequestHeaders(originRequest);
            }
            // Save originalUrl reference
            originalUrl = url;
            // console.log('DEBUG test proxification originalUrl', originalUrl)
            // Check if proxy is requested and available then proxify url
            if (requestOptions.proxy) {
                url = CommonRequest.proxifyUrl(url, requestOptions);
            }
            else if (CommonUtils.matchUrl(url, currentUrl) === false && CommonRequest.allowCrossOriginRequest === false) {
                // Force defaultProxy usage
                url = CommonRequest.proxifyUrl(url, Object.assign(Object.assign({}, requestOptions), { proxy: defaultProxy }));
            }
            // console.log('DEBUG test proxification proxifyUrl final', url)
            // Clone original request
            const body = ['GET', 'HEAD'].includes(originRequest.method) ? undefined : yield originRequest.blob();
            requestOptions.body = body;
            request = new Request(url, requestOptions);
            return request;
        });
    }
    /**
     * Proxify an URL using UWA proxy.
     *
     * @param {String} url - url to proxify
     * @param {String} options - some options from <request> options parameter
     * @return {String} full url to proxy with proxified options (header, data) has parameter.
     *
     * @alias CommonRequest.proxifyUrl
     */
    static proxifyUrl(url, options) {
        if (!url) {
            throw new Error('Bad or missing url argument.');
        }
        var proxyUrl, params = [], 
        // Alias
        proxy = options.proxy, toQueryString = CommonUtils.toQueryString, proxies = CommonRequest.proxies;
        // Add Proxy params
        if (proxy && proxies[proxy]) {
            // If not on native app && same domain then do not proxify url
            if (!Preferences.isNativeApp() || CommonUtils.matchUrl(url, window.location))
                return url;
            // Proxify Url
            proxyUrl = proxies[proxy];
            // console.log('DEBUG test proxification found proxyUrl', proxyUrl)
            // Avoid double proxy
            if (url.indexOf(proxyUrl) === 0) {
                return url;
            }
            // Omit port in url, e.g. 443
            if (url.startsWith('https://') && url.indexOf(':443') > -1) {
                url = url.replace(':443', '');
            }
            // Handle GET data for proxies by updating url
            if (options.body && options.method && options.method.toUpperCase() === 'GET') {
                // Add separator
                url += (url.indexOf('?') > -1 ? '&' : '?') + toQueryString(options.body);
                console.log('DEBUG test proxification handle get', url);
            }
            // Add proxy options param if proxy is object
            if (typeof options.proxy === 'object') {
                params.push(toQueryString(options.proxy));
            }
            // Forward headers throw get param only in proxy mode
            if (options.normalizedHeaders) {
                params.push(toQueryString(options.normalizedHeaders, 'headers'));
            }
            else if (options.headers) {
                params.push(toQueryString(options.headers, 'headers'));
            }
            // Add method params
            if (options.method) {
                params.push(toQueryString({
                    method: options.method
                }));
            }
            // Add proxified url as param
            params.push(toQueryString({
                url: url
            }));
            url = proxyUrl + '?' + params.join('&');
            // Avoid Url limit overflow
            if (url.length > 4096) {
                console.log("Error proxify url too long", url);
                throw new Error('Proxified url is more than 4096 characters, that is the limit for most of the browsers.');
            }
        }
        else {
            throw new Error('Invalid proxy');
        }
        return url;
    }
}
/**
 * Allow Cross Origin Request for Cross domain Ajax Request.
 */
CommonRequest.allowCrossOriginRequest = false;
export default CommonRequest;
//# sourceMappingURL=common-request-proxification.js.map